// Centralized power-rankings loader and scoring.
// Replaces three near-identical implementations that used to live inline
// in HomePage, Profiles, and HallOfFame.

import { parseCSV } from './csv';

const CAREER_STATS_CSV = '/data/comprehensive_career_statistics.csv';

// The CSV stores full names ('Kyle Teichmann') and the per-year casing
// ('kevin'). MANAGERS keys use short names ('Kyle', 'Kevin'). Normalize
// so ManagerProfile / Managers can look up by the short name.
const NAME_NORMALIZE = {
  'Kyle Teichmann': 'Kyle',
  'kyle Teichmann': 'Kyle',
  'Ryan Young': 'Ryan',
  'kevin': 'Kevin',
  'Sam': 'Samuel',
};

export function normalizeManagerName(name) {
  if (name === null || name === undefined) return name;
  const trimmed = String(name).trim();
  return NAME_NORMALIZE[trimmed] || trimmed;
}

function numberValue(value) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function integerValue(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function powerScore(winPercentage, championships, seasonsPlayed) {
  return 500 + (winPercentage * 2) + (championships * 50) + (seasonsPlayed * 10);
}

export function powerTier(score) {
  if (score >= 800) return 'Elite';
  if (score >= 750) return 'Excellent';
  if (score >= 700) return 'Good';
  if (score >= 650) return 'Average';
  if (score >= 600) return 'Below Average';
  return 'Poor';
}

export async function loadCareerRankings() {
  const response = await fetch(CAREER_STATS_CSV);
  if (!response.ok) {
    throw new Error('Failed to load comprehensive career statistics');
  }

  const rows = parseCSV(await response.text());
  const rankings = [];

  rows.forEach((row) => {
    if (row.manager === 'SYSTEM_VALIDATION') return;
    if (!row.manager) return;

    const championships = integerValue(row.total_championships);
    const winPercentage = numberValue(row.win_percentage);
    const seasonsPlayed = integerValue(row.total_seasons);
    const score = powerScore(winPercentage, championships, seasonsPlayed);

    rankings.push({
      manager: normalizeManagerName(row.manager),
      power_score: score,
      tier: powerTier(score),
      narrative: `${championships} titles, ${winPercentage}% win rate`,
      stats: {
        seasons_played: seasonsPlayed,
        years_active: row.years_active || '',
        championships,
        championship_years: String(row.championship_years || '')
          .split('|')
          .map((year) => integerValue(year))
          .filter(Boolean),
        win_percentage: winPercentage,
        total_wins: integerValue(row.total_wins),
        total_losses: integerValue(row.total_losses),
        total_games: integerValue(row.total_games),
        total_points: numberValue(row.total_points_for),
        average_points_per_season: numberValue(row.avg_points_per_season),
        average_points_per_game: numberValue(row.avg_points_per_game),
        best_season_wins: integerValue(row.best_season_wins),
        best_season_points: numberValue(row.best_season_points),
        best_season_year: integerValue(row.best_season_year),
        worst_season_wins: integerValue(row.worst_season_wins),
        worst_season_points: numberValue(row.worst_season_points),
        worst_season_year: integerValue(row.worst_season_year),
        playoff_appearances: integerValue(row.playoff_appearances),
        playoff_rate: numberValue(row.playoff_rate),
        playoff_success_rate: numberValue(row.playoff_success_rate),
        longest_win_streak: integerValue(row.longest_win_streak),
        scoring_vs_league_average: numberValue(row.percentage_above_average),
        validation_status: row.validation_status || '',
        data_sources: String(row.data_sources || '').split('|').filter(Boolean),
      },
    });
  });

  rankings.sort((a, b) => b.power_score - a.power_score);
  rankings.forEach((m, idx) => { m.rank = idx + 1; });

  return rankings;
}
